/**
 * DOM utility functions to replace jQuery-like functionality
 */

export class DomUtils {
    /**
     * Get element by ID
     */
    static getElementById(id: string): HTMLElement | null {
        return document.getElementById(id);
    }

    /**
     * Get elements by class name
     */
    static getElementsByClassName(className: string): HTMLCollectionOf<Element> {
        return document.getElementsByClassName(className);
    }

    /**
     * Trim string
     */
    static trim(str: string | null | undefined): string {
        return str ? str.toString().trim() : '';
    }

    /**
     * Add CSS class to element
     */
    static addClass(element: HTMLElement | null, className: string): void {
        if (element && !element.classList.contains(className)) {
            element.classList.add(className);
        }
    }

    /**
     * Remove CSS class from element
     */
    static removeClass(element: HTMLElement | null, className: string): void {
        if (element && element.classList.contains(className)) {
            element.classList.remove(className);
        }
    }

    /**
     * Check if element has CSS class
     */
    static hasClass(element: HTMLElement | null, className: string): boolean {
        return element ? element.classList.contains(className) : false;
    }

    /**
     * Get value from input element
     */
    static getValue(element: HTMLInputElement | HTMLSelectElement | null): string {
        return element ? element.value : '';
    }

    /**
     * Set value to input element
     */
    static setValue(element: HTMLInputElement | HTMLSelectElement | null, value: string): void {
        if (element) {
            element.value = value;
        }
    }

    /**
     * Show element
     */
    static showElement(element: HTMLElement | null): void {
        if (element) {
            element.style.display = 'block';
        }
    }

    /**
     * Hide element
     */
    static hideElement(element: HTMLElement | null): void {
        if (element) {
            element.style.display = 'none';
        }
    }

    /**
     * Show elements by class name
     */
    static showElementsByClass(className: string): void {
        const elements = this.getElementsByClassName(className);
        for (let i = 0; i < elements.length; i++) {
            this.showElement(elements[i] as HTMLElement);
        }
    }

    /**
     * Hide elements by class name
     */
    static hideElementsByClass(className: string): void {
        const elements = this.getElementsByClassName(className);
        for (let i = 0; i < elements.length; i++) {
            this.hideElement(elements[i] as HTMLElement);
        }
    }

    /**
     * Toggle multiple CSS classes on element
     */
    static toggleClasses(element: HTMLElement | null, addClass: string[], removeClass: string[]): void {
        if (!element) return;

        removeClass.forEach((cls) => element.classList.remove(cls));
        addClass.forEach((cls) => element.classList.add(cls));
    }

    /**
     * Clear element content
     */
    static clearContent(element: HTMLElement | null): void {
        if (element) {
            element.innerHTML = '';
        }
    }

    /**
     * Set element content
     */
    static setContent(element: HTMLElement | null, content: string): void {
        if (element) {
            element.innerHTML = content;
        }
    }

    /**
     * Append content to element
     */
    static appendContent(element: HTMLElement | null, content: string): void {
        if (element) {
            element.innerHTML += content;
        }
    }

    /**
     * Create and append option to select element
     */
    static appendOption(selectElement: HTMLSelectElement | null, value: string, text: string): void {
        if (selectElement) {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = text;
            selectElement.appendChild(option);
        }
    }

    /**
     * Clear all options from select except the first one
     */
    static clearSelectOptions(selectElement: HTMLSelectElement | null, keepFirstOption: boolean = true): void {
        if (!selectElement) return;

        const startIndex = keepFirstOption ? 1 : 0;
        while (selectElement.children.length > startIndex) {
            selectElement.removeChild(selectElement.lastChild!);
        }
    }
}
